import { createFileRoute } from "@tanstack/react-router";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { BlogByline } from "@/components/BlogByline";
import { ReadWithLlm } from "@/components/ReadWithLlm";
import { RelatedReading } from "@/components/RelatedReading";
import { relatedArticles } from "@/lib/clusters";
import { buildBlogHead } from "@/lib/blogMeta";
import manuscript from "@/content/magnesium-for-migraines.md?raw";
import hero from "@/assets/blog/migraine-hero-1600.webp.asset.json";
import heroSmall from "@/assets/blog/migraine-hero-768.webp.asset.json";
import deficiency from "@/assets/blog/migraine-deficiency-1600.webp.asset.json";
import deficiencySmall from "@/assets/blog/migraine-deficiency-768.webp.asset.json";
import forms from "@/assets/blog/migraine-forms-1600.webp.asset.json";
import formsSmall from "@/assets/blog/migraine-forms-768.webp.asset.json";
import timeline from "@/assets/blog/migraine-timeline-1600.webp.asset.json";
import timelineSmall from "@/assets/blog/migraine-timeline-768.webp.asset.json";
import combinations from "@/assets/blog/migraine-combinations-1600.webp.asset.json";
import combinationsSmall from "@/assets/blog/migraine-combinations-768.webp.asset.json";
import faqImage from "@/assets/blog/migraine-faq-1600.webp.asset.json";
import faqSmall from "@/assets/blog/migraine-faq-768.webp.asset.json";

const SLUG = "magnesium-for-migraines";
const TITLE = "Magnesium for Migraines: Which Form, How Much & Does It Work? - TruWe";
const DESCRIPTION = "Thinking about magnesium for migraines? TruWe reviews what studies found, which forms and doses were tested, side effects and who should be careful.";
const HEADLINE = "Magnesium for Migraines: Benefits, Best Forms & Dosage. See What Research Says";
const PUBLISHED = "2026-09-30";
const images: Record<string, { url: string; small: string }> = {
  "migraine-deficiency": { url: deficiency.url, small: deficiencySmall.url },
  "migraine-forms": { url: forms.url, small: formsSmall.url },
  "migraine-timeline": { url: timeline.url, small: timelineSmall.url },
  "migraine-combinations": { url: combinations.url, small: combinationsSmall.url },
  "migraine-faq": { url: faqImage.url, small: faqSmall.url },
};

export const Route = createFileRoute("/blog/magnesium-for-migraines")({
  component: Article,
  head: () => {
    const base = buildBlogHead({
      slug: SLUG,
      title: TITLE,
      description: DESCRIPTION,
      ogTitle: HEADLINE,
      ogDescription: DESCRIPTION,
      headline: HEADLINE,
      breadcrumbName: "Magnesium for Migraines",
      datePublished: PUBLISHED,
      dateModified: PUBLISHED,
      authorName: "TruWe",
      articleType: "BlogPosting",
      faqs: [],
      ogImage: `https://truwe.in${hero.url}`,
    });
    return {
      ...base,
      meta: [
        ...base.meta,
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: `https://truwe.in${hero.url}` },
      ],
    };
  },
});

function Article() {
  return (
    <article className="bg-background pb-24 pt-28 md:pt-36">
      <div className="mx-auto max-w-3xl px-4 md:px-8">
        <p className="text-xs font-semibold uppercase text-brand">Clinical Library · Minerals</p>
        <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight text-brand-deep md:text-5xl">
          {HEADLINE}
        </h1>
        <BlogByline author="TruWe" date={PUBLISHED} readingTime="9 min read" sources="American Headache Society, The Migraine Trust, NIH Office of Dietary Supplements and peer-reviewed reviews; sources linked below." />
        <div className="mt-4"><ReadWithLlm title={HEADLINE} url={`https://truwe.in/blog/${SLUG}`} kind="article" /></div>
        <img
          src={hero.url}
          srcSet={`${heroSmall.url} 768w, ${hero.url} 1600w`}
          sizes="(max-width: 768px) 100vw, 768px"
          alt="Illustrated overview of magnesium and migraine research"
          width={1600}
          height={857}
          fetchPriority="high"
          decoding="async"
          className="mt-8 h-auto w-full rounded-md border border-border"
        />
        <div className="mt-8 text-base leading-8 text-foreground/85 md:text-[17px]">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
            h2: ({ children }) => <h2 className="mb-4 mt-12 font-display text-2xl font-extrabold leading-tight text-brand-deep md:text-3xl">{children}</h2>,
            h3: ({ children }) => <h3 className="mb-3 mt-8 font-display text-xl font-bold text-brand-deep">{children}</h3>,
            p: ({ children }) => <p className="my-4">{children}</p>,
            ul: ({ children }) => <ul className="my-5 list-disc space-y-2 pl-6 marker:text-brand">{children}</ul>,
            li: ({ children }) => <li className="pl-1">{children}</li>,
            strong: ({ children }) => <strong className="font-semibold text-brand-deep">{children}</strong>,
            a: ({ href, children }) => <a href={href} rel={href?.startsWith("http") ? "noopener noreferrer" : undefined} target={href?.startsWith("http") ? "_blank" : undefined} className="text-brand underline underline-offset-2 hover:text-brand-deep">{children}</a>,
            img: ({ src, alt }) => {
              const image = images[src ?? ""];
              if (!image) return null;
              return <img src={image.url} srcSet={`${image.small} 768w, ${image.url} 1600w`} sizes="(max-width: 768px) 100vw, 768px" alt={alt ?? ""} width={1600} height={857} loading="lazy" decoding="async" className="my-8 h-auto w-full rounded-md border border-border" />;
            },
            table: ({ children }) => <div className="my-6 overflow-x-auto border-y border-border"><table className="w-full min-w-[560px] border-collapse text-left text-sm leading-6">{children}</table></div>,
            th: ({ children }) => <th className="border-b border-border bg-muted/30 px-4 py-3 font-semibold text-brand-deep">{children}</th>,
            td: ({ children }) => <td className="border-b border-border px-4 py-3 align-top">{children}</td>,
          }}>{manuscript.replace(/^# .+\n/, "")}</ReactMarkdown>
        </div>
        <RelatedReading articles={relatedArticles(SLUG)} />
      </div>
    </article>
  );
}